import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import API_CONFIG from './config/api';
import CreationWizard, { ReviewCard } from './CreationWizard';

const normalizarIdUsuario = (usuario) => usuario?._id || usuario?.id || '';
const nomeCargo = (usuario) => {
  const cargo = String(usuario?.cargo || usuario?.tipo || 'Sem cargo definido').trim();
  if (!cargo) return 'Sem cargo definido';
  const mapa = { super_admin: 'Super Admin', admin_loja: 'Admin Loja', gerente: 'Gerente', analista: 'Analista', vendedor: 'Vendedor', consultor: 'Consultor' };
  return mapa[cargo.toLowerCase()] || cargo.charAt(0).toUpperCase() + cargo.slice(1);
};

const NovaFicha = ({ isOpen, onClose, onSubmit, user }) => {
  const [formData, setFormData] = useState({
    vendedor_id: '',
    nomeCompleto: '',
    cpf: '',
    dataNascimento: '',
    temCnh: '',
    cnhObservacao: '',
    telefone: '',
    email: '',
    dadosConsultaCpf: {}
  });

  const [step, setStep] = useState(0);
  const consultaRef = useRef(0);
  const [consultandoCpf, setConsultandoCpf] = useState(false);
  const [mensagemCpf, setMensagemCpf] = useState('');
  useEffect(() => () => { consultaRef.current += 1; }, []);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);

  // Carregar usuários da loja quando o modal abrir
  useEffect(() => {
    if (isOpen) {
      carregarUsuarios();
      // Definir vendedor padrão baseado no tipo de usuário
      if (user.tipo === 'vendedor') {
        setFormData(prev => ({
          ...prev,
          vendedor_id: normalizarIdUsuario(user)
        }));
      }
    }
  }, [isOpen, user]);

  // Função para carregar usuários da mesma loja
  const carregarUsuarios = async () => {
    setLoadingUsuarios(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_CONFIG.API_URL}/usuarios`, {
        params: {
          status: 'ativo',
          limit: 5000
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        const usuariosDisponiveis = Array.isArray(response.data.data) ? response.data.data : [];
        usuariosDisponiveis.sort((a, b) => `${a.nome || ''}`.localeCompare(`${b.nome || ''}`, 'pt-BR'));
        setUsuarios(usuariosDisponiveis);
      }
    } catch (error) {
      setUsuarios([]);
    } finally {
      setLoadingUsuarios(false);
    }
  };

  // Função para validar CPF
  const validarCPF = (cpf) => {
    cpf = cpf.replace(/\D/g, ''); // Remove caracteres não numéricos

    if (cpf.length !== 11) return false;

    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1{10}$/.test(cpf)) return false;

    // Valida primeiro dígito verificador
    let soma = 0;
    for (let i = 0; i < 9; i++) {
      soma += parseInt(cpf.charAt(i)) * (10 - i);
    }
    let resto = soma % 11;
    let digitoVerificador1 = resto < 2 ? 0 : 11 - resto;

    if (parseInt(cpf.charAt(9)) !== digitoVerificador1) return false;

    // Valida segundo dígito verificador
    soma = 0;
    for (let i = 0; i < 10; i++) {
      soma += parseInt(cpf.charAt(i)) * (11 - i);
    }
    resto = soma % 11;
    let digitoVerificador2 = resto < 2 ? 0 : 11 - resto;

    return parseInt(cpf.charAt(10)) === digitoVerificador2;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Limpar erro do campo quando usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCnhChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      temCnh: value,
      cnhObservacao: value === 'outros' ? prev.cnhObservacao : ''
    }));
  };

  const formatCPF = (value) => {
    const numericValue = value.replace(/\D/g, '');
    return numericValue
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  };

  const formatTelefone = (value) => {
    const numericValue = value.replace(/\D/g, '');
    return numericValue
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1');
  };

  const handleCPFChange = (e) => {
    consultaRef.current += 1;
    setConsultandoCpf(false);
    setMensagemCpf('');
    const formatted = formatCPF(e.target.value);
    setFormData(prev => ({
      ...prev,
      cpf: formatted,
      dadosConsultaCpf: {}
    }));

    if (errors.cpf) {
      setErrors(prev => ({
        ...prev,
        cpf: ''
      }));
    }
  };

  const handleTelefoneChange = (e) => {
    const formatted = formatTelefone(e.target.value);
    setFormData(prev => ({
      ...prev,
      telefone: formatted
    }));

    if (errors.telefone) {
      setErrors(prev => ({
        ...prev,
        telefone: ''
      }));
    }
  };

  const consultarCpf = async () => {
    if (consultandoCpf) return;
    const cpf = formData.cpf.replace(/\D/g, '');
    if (!validarCPF(cpf)) {
      setMensagemCpf('Informe um CPF válido para consultar.');
      return;
    }
    const consulta = ++consultaRef.current;
    setConsultandoCpf(true);
    setMensagemCpf('Consultando CPF…');
    try {
      const response = await axios.post(`${API_CONFIG.API_URL}/negociacoes/consultar-cpf`, { cpf }, {
        headers: { 'x-auth-token': localStorage.getItem('token') }, timeout: 35000
      });
      if (consulta !== consultaRef.current) return;
      if (!response.data.success) throw new Error(response.data.message || 'Não foi possível consultar o CPF.');
      const dados = response.data.data || {};
      setFormData(prev => prev.cpf.replace(/\D/g, '') === cpf ? {
        ...prev,
        nomeCompleto: prev.nomeCompleto || dados.nome || '',
        dataNascimento: prev.dataNascimento || dados.dataNascimento || '',
        telefone: prev.telefone || (dados.telefone ? formatTelefone(dados.telefone) : ''),
        dadosConsultaCpf: dados
      } : prev);
      setErrors({});
      setMensagemCpf('');
    } catch (error) {
      if (consulta === consultaRef.current) setMensagemCpf(`${error.response?.data?.message || error.message || 'Não foi possível consultar o CPF.'} Você pode preencher manualmente.`);
    } finally {
      if (consulta === consultaRef.current) setConsultandoCpf(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.vendedor_id) {
      newErrors.vendedor_id = 'Vendedor responsável é obrigatório';
    }

    // Validação de Nome
    if (!formData.nomeCompleto?.trim()) {
      newErrors.nomeCompleto = 'Nome completo é obrigatório';
    }

    // Validação de Email (opcional)
    if (formData.email && formData.email.trim() && !formData.email.includes('@')) {
      newErrors.email = 'Email deve conter o símbolo @';
    }

    // Validação de CPF
    if (!formData.cpf.trim()) {
      newErrors.cpf = 'CPF é obrigatório';
    } else if (!validarCPF(formData.cpf)) {
      newErrors.cpf = 'CPF inválido';
    }

    // Validação de Data de Nascimento
    if (!formData.dataNascimento) {
      newErrors.dataNascimento = 'Data de nascimento é obrigatória';
    } else {
      const dataNasc = new Date(formData.dataNascimento);
      const hoje = new Date();
      let idade = hoje.getFullYear() - dataNasc.getFullYear();
      const mesAtual = hoje.getMonth();
      const diaAtual = hoje.getDate();
      const mesNasc = dataNasc.getMonth();
      const diaNasc = dataNasc.getDate();

      if (mesAtual < mesNasc || (mesAtual === mesNasc && diaAtual < diaNasc)) {
        idade--;
      }

      if (idade < 18) {
        newErrors.dataNascimento = 'Cliente deve ter no mínimo 18 anos';
      } else if (idade > 90) {
        // Confirmar se cliente realmente tem mais de 90 anos
        const confirmar = window.confirm(`Cliente tem ${idade} anos de idade. Deseja continuar?`);
        if (!confirmar) {
          newErrors.dataNascimento = 'Confirme a data de nascimento';
        }
      }
    }

    if (!formData.temCnh) {
      newErrors.temCnh = 'Campo CNH é obrigatório';
    }

    if (!formData.telefone.trim()) {
      newErrors.telefone = 'Telefone é obrigatório';
    } else if (formData.telefone.replace(/\D/g, '').length < 10) {
      newErrors.telefone = 'Telefone deve ter pelo menos 10 dígitos';
    }

    if (Object.keys(newErrors).length) setStep(0);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading || consultandoCpf) return;
    if (step < 1) {
      if (validateForm()) setStep(step + 1);
      return;
    }
    if (!validateForm()) return;

    setLoading(true);

    try {
      const dadosCpf = formData.dadosConsultaCpf || {};
      const [cidadeLegada = '', estadoLegado = ''] = `${dadosCpf.cidadeEstado || ''}`
        .split('/')
        .map(parte => parte.trim());
      const fichaData = {
        usuario_id: formData.vendedor_id, // ID do vendedor responsável
        vendedor_id: formData.vendedor_id,
        vendedor_nome: usuarios.find((u) => `${normalizarIdUsuario(u)}` === `${formData.vendedor_id}`)?.nome || '',
        cliente: {
          nome: formData.nomeCompleto,
          cpf: formData.cpf,
          data_nascimento: formData.dataNascimento,
          telefone: formData.telefone,
          celular: formData.telefone,
          email: formData.email || '',
          rg: dadosCpf.rg || '',
          nome_pai: dadosCpf.nomePai || '',
          nome_mae: dadosCpf.nomeMae || '',
          estado_civil: dadosCpf.estadoCivil || '',
          naturalidade: dadosCpf.naturalidade || '',
          cnh: formData.temCnh === 'sim' ? 'SIM' : (formData.temCnh === 'nao' ? 'NAO' : formData.cnhObservacao),
          possui_cnh: formData.temCnh === 'sim' ? true : (formData.temCnh === 'nao' ? false : null),
          endereco: {
            cep: dadosCpf.cep || '',
            rua: dadosCpf.rua || '',
            numero: dadosCpf.numero || '',
            bairro: dadosCpf.bairro || '',
            cidade: dadosCpf.cidade || cidadeLegada,
            estado: dadosCpf.estado || estadoLegado
          }
        },
        dados_profissionais: {
          cargo: dadosCpf.profissao || ''
        },
        veiculo: {
          marca: '',
          modelo: '',
          ano: '',
          valor: 0
        },
        financiamento: {
          valor_financiado: 0,
          prazo: 0,
          entrada: 0
        },
        observacoes: ''
      };

      const resultado = await onSubmit(fichaData);
      if (resultado === false) throw new Error('Não foi possível criar a ficha. Confira os dados e tente novamente.');
      handleClose();
    } catch (error) {
      setErrors({ geral: error.response?.data?.message || error.message || 'Erro ao criar ficha' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    consultaRef.current += 1;
    setConsultandoCpf(false);
    setMensagemCpf('');
    setStep(0);
    setFormData({
      vendedor_id: '',
      nomeCompleto: '',
      cpf: '',
      dataNascimento: '',
      temCnh: '',
      cnhObservacao: '',
      telefone: '',
      email: '',
      dadosConsultaCpf: {}
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <CreationWizard title="Nova ficha" steps={['Dados', 'Revisão']} step={step} onStep={setStep} onClose={handleClose} onSubmit={handleSubmit} busy={loading || consultandoCpf} submitLabel="Criar ficha" error={errors.geral} headingAside={step === 0 && user.tipo !== 'vendedor' && (
            <div className="form-step">
              <h3>Responsável pela Ficha</h3>

              <div className="form-group">
                <label htmlFor="vendedor_id">
                  Vendedor Responsável <span className="required">*</span>
                </label>
                {loadingUsuarios ? (
                  <div className="loading-select">Carregando usuários...</div>
                ) : (
                  <select
                    id="vendedor_id"
                    name="vendedor_id"
                    value={formData.vendedor_id}
                    onChange={handleChange}
                    className={errors.vendedor_id ? 'error' : ''}
                  >
                    <option value="">Selecione o vendedor...</option>
                    {Object.entries(usuarios.reduce((grupos, usuario) => {
                      const cargo = nomeCargo(usuario);
                      if (!grupos[cargo]) grupos[cargo] = [];
                      grupos[cargo].push(usuario);
                      return grupos;
                    }, {})).sort(([a], [b]) => a.localeCompare(b, 'pt-BR')).map(([cargo, membros]) => (
                      <optgroup key={cargo} label={cargo}>
                        {membros.sort((a, b) => `${a.nome || ''}`.localeCompare(`${b.nome || ''}`, 'pt-BR')).map(usuario => (
                          <option key={normalizarIdUsuario(usuario)} value={normalizarIdUsuario(usuario)}>{usuario.nome}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                )}
                {errors.vendedor_id && <span className="error-text">{errors.vendedor_id}</span>}
              </div>
            </div>
          )} description={['', 'Confira os dados antes de criar a ficha.'][step]}>
          {step === 0 && <div className="form-step">
            <h3>Dados Pessoais</h3>
              <div className="form-group">
                <label htmlFor="cpf">
                  CPF <span className="required">*</span>
                </label>
                <div className="cw-cpf-search"><input
                  type="text"
                  id="cpf"
                  name="cpf"
                  value={formData.cpf}
                  onChange={handleCPFChange}
                  className={errors.cpf ? 'error' : ''}
                  placeholder="000.000.000-00"
                  maxLength="14"
                />
                <button type="button" className="cw-secondary" onClick={consultarCpf} aria-label="Consultar CPF" title="Consultar CPF" aria-busy={consultandoCpf}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 4.5 4.5" /></svg></button></div>
                {mensagemCpf && <p className="cw-note" role="status">{mensagemCpf}</p>}
                {errors.cpf && <span className="error-text">{errors.cpf}</span>}
              </div>



            <div className="nova-ficha-row">
              <div className="form-group">
                <label htmlFor="nomeCompleto">
                  Nome Completo
                </label>
                <input
                  type="text"
                  id="nomeCompleto"
                  name="nomeCompleto"
                  value={formData.nomeCompleto}
                  onChange={handleChange}
                  className={errors.nomeCompleto ? 'error' : ''}
                  placeholder="Digite o nome completo"
                />
                {errors.nomeCompleto && <span className="error-text">{errors.nomeCompleto}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="temCnh">
                  Tem CNH? <span className="required">*</span>
                </label>
                <select
                  id="temCnh"
                  name="temCnh"
                  value={formData.temCnh}
                  onChange={handleCnhChange}
                  className={errors.temCnh ? 'error' : ''}
                >
                  <option value="">Selecione...</option>
                  <option value="sim">Sim</option>
                  <option value="nao">Não</option>
                  <option value="outros">Outros</option>
                </select>
                {errors.temCnh && <span className="error-text">{errors.temCnh}</span>}
              </div>
            </div>

            <div className="nova-ficha-row">
              <div className="form-group">
                <label htmlFor="dataNascimento">
                  Data de Nascimento <span className="required">*</span>
                </label>
                <input
                  type="date"
                  id="dataNascimento"
                  name="dataNascimento"
                  value={formData.dataNascimento}
                  onChange={handleChange}
                  className={errors.dataNascimento ? 'error' : ''}
                />
                {errors.dataNascimento && <span className="error-text">{errors.dataNascimento}</span>}
              </div>
            </div>

            {formData.temCnh === 'outros' && (
              <div className="form-group">
                <label htmlFor="cnhObservacao">
                  Observação sobre CNH
                </label>
                <input
                  type="text"
                  id="cnhObservacao"
                  name="cnhObservacao"
                  value={formData.cnhObservacao}
                  onChange={handleChange}
                  placeholder="Descreva a situação da CNH"
                />
              </div>
            )}

          </div>}
          {step === 0 && <div className="form-step">
            <div className="nova-ficha-row">
              <div className="form-group">
                <label htmlFor="telefone">
                  Telefone <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="telefone"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleTelefoneChange}
                  className={errors.telefone ? 'error' : ''}
                  placeholder="(11) 99999-9999"
                  maxLength="15"
                />
                {errors.telefone && <span className="error-text">{errors.telefone}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="email@exemplo.com"
                />
              </div>
            </div>
          </div>}
          {step === 1 && <>
            <ReviewCard title="Cliente" onEdit={() => setStep(0)} rows={[
              ['Nome', formData.nomeCompleto], ['CPF', formData.cpf], ['Nascimento', formData.dataNascimento.split('-').reverse().join('/')], ['CNH', formData.temCnh === 'sim' ? 'Sim' : formData.temCnh === 'nao' ? 'Não' : formData.cnhObservacao]
            ]} />
            <ReviewCard title="Contato e responsável" onEdit={() => setStep(0)} rows={[
              ['Telefone', formData.telefone], ['Email', formData.email], ['Responsável', usuarios.find(u => String(normalizarIdUsuario(u)) === String(formData.vendedor_id))?.nome || user.nome]
            ]} />
          </>}
    </CreationWizard>
  );
};

export default NovaFicha;
